<script lang="ts" setup>
/**
 * TimeController - Actor 컴포넌트 조합 버전
 *
 * Actor 컴포넌트:
 * - LiveUpdater: 백그라운드 시간 갱신 (renderless)
 * - TimeControls: 시간 제어 버튼 (뒤로/일시정지/앞으로/새로고침)
 * - PeriodSelector: 기간 드롭다운
 * - CalendarSelector: 캘린더 선택
 * - TimePeriodDisplay: 시간 표시
 */
import { computed, ref } from 'vue';
import type { PaTimePeriodFilterOptions, TimePeriodData } from '@/types/common.types';
import type { TimezoneName } from '@/common/utils';
import { useMessageBox } from '@/common/inject/useMessageBox';
import { useInternational } from '@/common/locale';
import { parsePeriod } from '@/common/utils/core/time/parse';
import { findNearestSupportedPeriod } from '@/common/utils/core/time/conversion';
import { getInitPeriodInfoByServerTime } from '@/common/api/time/getServerCurrentTime';

// Actor Components
import LiveUpdater from './actors/LiveUpdater.vue';
import TimeControls from './actors/TimeControls.vue';
import PeriodSelector from './actors/PeriodSelector.vue';
import CalendarSelector from './actors/CalendarSelector.vue';
import TimePeriodDisplay from './actors/TimePeriodDisplay.vue';

// Internal Components
import LiveTag from './components/LiveTag.vue';
import TimePolicyPopup from './components/TimePolicyPopup.vue';

// Local dependencies
import { useTimePolicyPopup } from './composables/useTimePolicyPopup';
import {
  getInitPeriodInfo,
  formatTimeRangeToUtc,
  getCurrentTimeRange,
  getFixedTimesByReadonlyTimes,
  getFormalPeriod,
  createTimePeriodInfoFromCurrentTime,
} from './utils';
import type { TimePeriodInfo, TimePeriodOptions, Mode } from './types';
import { MODE } from './types';
import {
  DEFAULT_PERIOD_ITEMS,
  GLOBAL_TIME_LIVE_FORMAT,
  GLOBAL_TIME_RANGE_FORMAT,
} from './constants';
import { MODE_CONFIG, type ModeConfig, type ValidMode } from './modeConfig';

// ============================================================
// Props & Emits
// ============================================================

interface Props {
  mode?: Mode;
  periodInfo?: TimePeriodInfo;
  periodItems?: Readonly<TimePeriodData[]>;
  displayDateOnly?: boolean;
  timeUnit?: 'day' | 'sec';
  hideDropdownWrapper?: boolean;
  options?: TimePeriodOptions;
  disabled?: boolean;
  isError?: boolean;
  size?: 'sm' | 'md';
  disableTimePolicyPopup?: boolean;
  calendarPos?: 'left' | 'right';
  /** 시간 계산·표시 기준 타임존 */
  timezone?: TimezoneName;

  paTimeFilterOptions?: PaTimePeriodFilterOptions;
}

interface Emit {
  (e: 'update-period-info', value: TimePeriodInfo): void;
}

const props = withDefaults(defineProps<Props>(), {
  periodInfo: () => getInitPeriodInfo(),
  periodItems: () => DEFAULT_PERIOD_ITEMS,
  options: () => ({
    interval: 'I5s',
    timeRangeSecLimit: undefined,
    minTimeRangeSecLimit: undefined,
    liveFormat: GLOBAL_TIME_LIVE_FORMAT,
    rangeFormat: GLOBAL_TIME_RANGE_FORMAT,
  }),
  displayDateOnly: false,
  timeUnit: undefined,
  hideDropdownWrapper: false,
  disabled: false,
  isError: false,
  size: 'sm',
  disableTimePolicyPopup: false,
  paTimeFilterOptions: () => ({}),
  calendarPos: 'left',
  timezone: undefined,
});

const emit = defineEmits<Emit>();

const { promiseConfirmMsg } = useMessageBox();
const { t } = useInternational();

const tcHeight = computed(() => (props.size === 'md' ? '28px' : '24px'));

// ============================================================
// State
// ============================================================

const timePeriodInfo = computed<TimePeriodInfo>({
  get: () => {
    const info = props.periodInfo;

    // MODE.PERIOD에서 isPaused=false인 경우 자동 수정
    if (mode.value === MODE.PERIOD && !info.isPaused && info.timePeriod !== 'pa') {
      if (import.meta.env.DEV) {
        console.warn(
          '[TimeController] MODE.PERIOD에서는 isPaused: true로 초기화해야 합니다. 자동으로 isPaused: true로 설정됩니다.',
        );
      }
      return { ...info, isPaused: true };
    }

    return info;
  },
  set: (info) => {
    emit('update-period-info', info);
  },
});

const liveUpdaterRef = ref<InstanceType<typeof LiveUpdater> | null>(null);
const timeControlsRef = ref<InstanceType<typeof TimeControls> | null>(null);
const calendarSelectorRef = ref<InstanceType<typeof CalendarSelector> | null>(null);

// ============================================================
// Computed Properties
// ============================================================

/**
 * mode 계산: props.mode가 있으면 사용, 없으면 options에서 계산 (backward compatibility)
 */
const mode = computed<Mode>(() => {
  if (props.mode) {
    return props.mode;
  }

  if (import.meta.env.DEV) {
    console.warn(
      '[TimeController] mode가 설정되지 않았습니다. "mode" prop을 명시적으로 설정해주세요. 기본값 PERIOD로 동작합니다.',
    );
  }
  return MODE.PERIOD; // default
});

// ============================================================
// Mode Config (Strategy Pattern)
// ============================================================

/** 현재 모드에 해당하는 UI 설정 */
const config = computed<ModeConfig>(() => MODE_CONFIG[mode.value as ValidMode]);

// ============================================================
// UI Visibility Computed Properties (config 기반)
// ============================================================

/** 현재 기간이 Live 모드 제한 이내인지 */
const isLiveAllowed = computed(() => {
  const useLiveSecLimit = props.options?.useLiveSecLimit;
  if (!useLiveSecLimit) return true;

  const period = getFormalPeriod(timePeriodInfo.value.timePeriod);
  const { offset } = parsePeriod(period);
  return offset <= useLiveSecLimit;
});

/** Resume 시 확인 필요 여부
 * - Live 모드(useLiveUpdater)면 항상 handleResume 통해 resume (period 변환 보장)
 * - useLiveSecLimit 설정 + Live 제한 초과 시에도 확인 필요
 */
const preventAutoResume = computed(() => {
  return config.value.useLiveUpdater || (!!props.options?.useLiveSecLimit && !isLiveAllowed.value);
});

const isLiveUpdateDisabled = computed(() => {
  return (
    props.disabled ||
    !config.value.useLiveUpdater ||
    timePeriodInfo.value.timePeriod === 'pa' ||
    !isLiveAllowed.value
  );
});

const showNavigator = computed(() => {
  return config.value.controls.showNavigator;
});

const showLiveAnimation = computed(() => {
  return config.value.useLiveUpdater && !timePeriodInfo.value.isPaused;
});

// ============================================================
// Time Policy Popup
// ============================================================

const isTimePolicyDisabled = computed(
  () => props.disableTimePolicyPopup || mode.value === MODE.CURRENT,
);
const { timeControllerRef, timePolicyRef, hideTimePolicy, checkAndShowPolicy } =
  useTimePolicyPopup(isTimePolicyDisabled);

// ============================================================
// Event Handlers
// ============================================================

const handlePeriodSelect = (period: string) => {
  const { fromTime, toTime } = getCurrentTimeRange({
    period,
    timeFormat: props.paTimeFilterOptions?.timeFormat,
    fixedTimes: getFixedTimesByReadonlyTimes(props.options?.readonlyTimes),
    timezone: props.timezone,
  });

  // useLiveSecLimit 초과 시 Live 모드 비활성화
  const useLiveSecLimit = props.options?.useLiveSecLimit;
  const periodOffset = parsePeriod(getFormalPeriod(period)).offset;
  const exceedsLiveLimit = !!(useLiveSecLimit && periodOffset > useLiveSecLimit);

  timePeriodInfo.value = {
    timePeriod: period,
    fromTime,
    toTime,
    ...formatTimeRangeToUtc({ fromTime, toTime, timezone: props.timezone }),
    isPaused: !config.value.useLiveUpdater || exceedsLiveLimit,
  };
};

// CalendarSelector의 change-period 이벤트 핸들러 (period 인자 없음)
const handleCalendarPeriodChange = () => {
  const selectedPeriod = calendarSelectorRef.value?.selectedPeriod;
  if (selectedPeriod) {
    handlePeriodSelect(selectedPeriod);
  }
};

const handleCalendarChange = (payload: { fromTime: string; toTime: string; period: string }) => {
  timePeriodInfo.value = {
    timePeriod: payload.period,
    fromTime: payload.fromTime,
    toTime: payload.toTime,
    ...formatTimeRangeToUtc({
      fromTime: payload.fromTime,
      toTime: payload.toTime,
      timezone: props.timezone,
    }),
    isPaused: true,
  };
};

const handleBackwardNavigation = () => {
  liveUpdaterRef.value?.stop();
};

const handleForwardNavigation = () => {
  if (!timePeriodInfo.value.isPaused) {
    // Live 모드: >> 클릭 시점부터 5초 간격 재시작
    liveUpdaterRef.value?.start();
  }
};

const handleExceededCurrentTime = () => {
  const { timePeriod } = timePeriodInfo.value;
  const supportedPeriods = props.periodItems.map((item) => item.value);
  const effectivePeriod = findNearestSupportedPeriod(timePeriod, supportedPeriods);

  const { fromTime, toTime } = getCurrentTimeRange({
    period: effectivePeriod,
    timeFormat: props.paTimeFilterOptions?.timeFormat,
    fixedTimes: getFixedTimesByReadonlyTimes(props.options?.readonlyTimes),
    timezone: props.timezone,
  });

  timePeriodInfo.value = {
    timePeriod: effectivePeriod,
    fromTime,
    toTime,
    ...formatTimeRangeToUtc({ fromTime, toTime, timezone: props.timezone }),
    isPaused: false, // Live 모드로 복귀
  };

  // isPaused가 false로 변경되면 LiveUpdater의 watcher가 자동으로 start() 호출
};

/**
 * Resume 핸들러 - Live 제한 초과 시 확인 다이얼로그 표시
 * 복합 period(예: 'p23h 59m 49s')는 지원하는 period로 변환
 */
// handleResume 이 직접 timePeriodInfo 를 set 한 직후 짧은 시간 안에 도착하는 LiveUpdater
// 자동 onTick 은 같은 serverTime 의 redundant tick. 부모 props.periodInfo 와 LiveUpdater 의
// 첫 emit 비교가 우연히 일치해 onMounted 직후의 정상적인 첫 갱신을 막지 않도록,
// "TimeController 자체가 직접 set 한 직후" 의 redundant tick 만 차단하기 위한 추적.
let recentSelfSetInfo: { fromTime: string; toTime: string; at: number } | null = null;
const SELF_SET_GUARD_MS = 1000;

const setLivePeriodFromServer = async (period: string): Promise<void> => {
  const info = await getInitPeriodInfoByServerTime({
    timePeriod: period,
    isPaused: false,
  });
  recentSelfSetInfo = { fromTime: info.fromTime, toTime: info.toTime, at: Date.now() };
  timePeriodInfo.value = info;
};

const handleResume = async () => {
  const { timePeriod } = timePeriodInfo.value;
  const supportedPeriods = props.periodItems.map((item) => item.value);

  // 항상 findNearestSupportedPeriod 호출
  // - 복합 period(예: 'p23h 59m 49s')는 지원 period로 변환
  // - 일반 period는 그대로 반환 (지원 목록에 있으면)
  const effectivePeriod = findNearestSupportedPeriod(timePeriod, supportedPeriods);

  // isLiveAllowed가 true면 바로 resume
  // /v7/now 한 번으로 서버 시각 기반 timePeriodInfo 를 한 번에 set 한다.
  // (클라이언트 dayjs.tz() 기반 handlePeriodSelect + LiveUpdater 자동 onTick(서버 시각) 으로
  //  서로 다른 fromTime/toTime 두 번 emit 되어 부모 차트 API 가 중복 호출되는 흐름을 피한다.
  //  handleLiveUpdaterTick 측 가드는 같은 serverTime 도달 시 redundant tick 을 차단.)
  if (isLiveAllowed.value) {
    await setLivePeriodFromServer(effectivePeriod);
    return;
  }

  // 제한 초과: 확인 다이얼로그
  const confirmed = await promiseConfirmMsg({
    msgStr: t('MESSAGE.UI.LOGS.REAL_TIME'),
  });

  if (confirmed) {
    await setLivePeriodFromServer('p10m');
  }
  // 취소 시 아무 동작 없음 (현재 상태 유지)
};

const handleLiveUpdaterTick = (serverTime: string) => {
  const next = createTimePeriodInfoFromCurrentTime({
    currentTime: serverTime,
    timePeriod: timePeriodInfo.value.timePeriod,
    isPaused: false,
  });

  // handleResume 등 TimeController 가 방금 set 한 직후의 redundant tick 만 차단한다.
  // 부모 mount 직후 stale 한 props.periodInfo 와 LiveUpdater 첫 emit 이 우연히 같은 초로
  // 떨어져 정상 갱신이 막히는 경우 (예: AlertDetail History 탭 진입 시 5 초 동안 차트 멈춤)
  // 가 발생하지 않도록, self-set 시점을 timestamp 로 추적해 짧은 윈도우 안에서만 가드 적용.
  if (
    recentSelfSetInfo &&
    Date.now() - recentSelfSetInfo.at < SELF_SET_GUARD_MS &&
    recentSelfSetInfo.fromTime === next.fromTime &&
    recentSelfSetInfo.toTime === next.toTime
  ) {
    return;
  }

  timePeriodInfo.value = next;
};

// ============================================================
// Expose
// ============================================================

defineExpose({
  togglePause: () => {
    timeControlsRef.value?.togglePause();
  },
  refresh: () => {
    timeControlsRef.value?.refresh();
  },
});
</script>

<template>
  <div
    ref="timeControllerRef"
    class="time-controller"
    :class="[mode?.toLowerCase() ?? '', { 'calendar-right': props.calendarPos === 'right' }]"
    :style="{ '--cursor': mode === MODE.CURRENT ? 'pointer' : 'default', '--tc-height': tcHeight }"
    @mouseleave="hideTimePolicy"
  >
    <!-- LiveUpdater: 백그라운드 시간 갱신 (renderless) -->
    <live-updater
      ref="liveUpdaterRef"
      :is-paused="timePeriodInfo.isPaused"
      :interval="props.options?.interval || 'p5s'"
      :disabled="isLiveUpdateDisabled"
      @tick="handleLiveUpdaterTick"
    />

    <!-- MODE.CURRENT: TimePeriodDisplay (심플 표시) -->
    <template v-if="config.useTimePeriodDisplay">
      <time-period-display
        :period-info="timePeriodInfo"
        format="live"
        :live-format="props.options?.liveFormat"
        :range-format="props.options?.rangeFormat"
        :show-live-tag="true"
        :disabled="props.disabled"
        @mouseenter="checkAndShowPolicy"
      />
    </template>

    <!-- 기타 모드: 기간 선택 UI (Period 또는 Calendar) -->
    <div
      v-else
      :class="[
        'time-controller__period',
        {
          'time-controller__period--live': showLiveAnimation,
        },
      ]"
      @mouseenter="checkAndShowPolicy"
    >
      <!-- Live 태그 (pa 모드가 아닐 때) -->
      <live-tag
        v-if="config.showLiveTag && timePeriodInfo.timePeriod !== 'pa'"
        :disabled="props.disabled"
      />

      <!-- 캘린더 모드 -->
      <calendar-selector
        v-if="config.selectorType === 'calendar'"
        ref="calendarSelectorRef"
        v-model="timePeriodInfo"
        :items="props.periodItems"
        :display-date-only="props.displayDateOnly"
        :time-unit="props.timeUnit"
        :hide-dropdown-wrapper="props.hideDropdownWrapper"
        :max-range-sec="props.options?.timeRangeSecLimit"
        :min-range-sec="props.options?.minTimeRangeSecLimit"
        :use-input="config.showCalendarInput"
        :disabled="props.disabled"
        :is-error="props.isError"
        :use-live-updater="config.useLiveUpdater"
        :readonly-times="props.options?.readonlyTimes"
        :pa-time-filter-options="props.paTimeFilterOptions"
        :calendar-pos="props.calendarPos"
        :timezone="props.timezone"
        @change-period="handleCalendarPeriodChange"
        @change-period-by-calendar="handleCalendarChange"
        @dropdown-open="hideTimePolicy"
      />

      <!-- 기본 기간 선택 모드 -->
      <period-selector
        v-else-if="config.selectorType === 'period'"
        v-model="timePeriodInfo"
        :items="props.periodItems"
        :display-date-only="props.displayDateOnly"
        :disabled="props.disabled"
        @select="handlePeriodSelect"
        @dropdown-open="hideTimePolicy"
      />
    </div>

    <!-- 시간 제어 버튼들 -->
    <time-controls
      ref="timeControlsRef"
      v-model="timePeriodInfo"
      :disabled="props.disabled"
      :show-navigator="showNavigator"
      :show-refresh="config.controls.showRefresh"
      :disable-refresh="props.options?.disableRefresh"
      :always-show-play="config.controls.alwaysShowPlay"
      :max-time-range-sec-limit="props.options?.timeRangeSecLimit"
      :prevent-auto-resume="preventAutoResume"
      :show-pause="config.controls.showPause"
      :timezone="props.timezone"
      @backward="handleBackwardNavigation"
      @forward="handleForwardNavigation"
      @resume="handleResume"
      @exceeded-current-time="handleExceededCurrentTime"
    />

    <!-- 시간 정책 팝업 -->
    <teleport to="#popup">
      <time-policy-popup ref="timePolicyRef" />
    </teleport>
  </div>
</template>

<style lang="scss">
@property --animate-duration {
  syntax: '<angle>';
  initial-value: 0deg;
  inherits: false;
}

@property --gradient-color {
  syntax: '<color>';
  initial-value: var(--color-green-05);
}

@keyframes gradient-rotate {
  0% {
    --animate-duration: 0deg;
  }
  100% {
    --animate-duration: 360deg;
  }
}

@keyframes bg-pulse {
  0% {
    background-color: var(--color-gray-12-00);
  }
  50% {
    background-color: var(--bg-pulse-highlight, var(--color-green-09-00));
  }
  100% {
    background-color: var(--color-gray-12-00);
  }
}

@mixin animated-conic-border($edgeColor: var(--gradient-color)) {
  position: relative;
  padding: 1px;
  overflow: hidden;
  border-radius: 2px;

  &::before,
  &::after {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 2px;
    background-image: conic-gradient(
      from var(--animate-duration) at 50% 50%,
      var(--input-text-border-color) 0%,
      var(--input-text-border-color) 20%,
      #{$edgeColor} 50%,
      #{$edgeColor} 66%,
      var(--input-text-border-color) 100%
    );
    background-position: 0 0;
    background-size: 100% 100%;
    animation: gradient-rotate 3s infinite linear;
    content: '';
  }

  &::before {
    filter: blur(20px);
  }
}

@mixin pulsing-bg {
  background-color: var(--color-gray-12-00);
  animation: bg-pulse 3s ease-in-out infinite;
}
.time-controller {
  display: flex;
  gap: 6px;

  // TimePeriodDisplay overrides for this context
  .time-period-display {
    width: 200px;
  }

  &__period {
    .live-tag {
      display: none;
    }

    &--live {
      position: relative;

      .ev-select__wrapper {
        --gradient-color: var(--color-green-05);

        @include animated-conic-border(var(--gradient-color));

        &-inner {
          display: flex;
          position: relative;
          z-index: 1;
          align-items: center;
          width: 100%;
          height: 100%;

          @include pulsing-bg();
        }
      }

      .time-period__select {
        &:not(.disabled) {
          .ev-input:hover {
            background-color: transparent !important;
          }
        }
      }

      .open-dropdown {
        .ev-select__wrapper {
          --gradient-color: var(--color-blue-05);
          --bg-pulse-highlight: var(--color-blue-09-00);
        }
      }

      .live-tag {
        display: block;
        position: absolute;
        top: calc(var(--tc-height) / 2 - 8px);
        left: 43px;
        z-index: 2;

        + .time-period-v2 {
          .time-period__select {
            .ev-select__wrapper {
              .ev-input {
                height: calc(var(--tc-height) - 2px) !important;
                padding-left: 82px !important;
                border: none !important;
              }
            }
          }
        }
      }
    }

    .time-period-v2 {
      width: 280px;

      .time-period__select {
        width: 100% !important;
        .ev-select__wrapper {
          .ev-input {
            transition: padding-left 0.3s !important;
          }
        }
      }

      .ev-input {
        width: 100% !important;
        height: var(--tc-height) !important;
        font-size: 12px !important;
      }
    }
  }
}
</style>
